/**
 * Email 後台（條件規則、自動化流程、行銷信活動、收件人狀態）api、契約與 web 共用的值清單。
 *
 * 放 core 而不是 `@kiao-voice/api-contract`：apps/api 對契約是零 runtime 依賴（`apps/api/eslint.config.js`
 * 機器強制），validator 的 `vine.enum`、service 的執行期檢查都要讀得到值。契約的
 * `email-rules.ts`／`email-flows.ts`／`email-delivery.ts` 以 `import type` 再 re-export 同名型別。
 */

import { USER_USE_CASES } from './account.js'
import { PLAN_CODES } from './plan-limits.js'

/** 郵件條件可用的事件。每個 key 的資料來源與能不能數次數，由 api 的事件目錄決定。 */
export const EMAIL_RULE_EVENT_KEYS = [
  'signup',
  'voice',
  'transcript',
  'notes',
  'login',
  'used',
  'limit',
  'onboarding',
  'paid',
  'cancel',
  'renew_fail',
] as const
export type EmailRuleEventKey = (typeof EMAIL_RULE_EVENT_KEYS)[number]

/**
 * 流程觸發「名單上所有人」：流程變成執行中時，對象名單上的人全部加入。只能當流程觸發，
 * 不是規則事件，所以不在 {@link EMAIL_RULE_EVENT_KEYS} 裡（規則、等待事件、數字分段、目標都選不到）。
 */
export const EMAIL_FLOW_AUDIENCE_TRIGGER = 'audience' as const
/** 流程觸發節點的值：可觸發的規則事件（由 api 事件目錄的 `canTrigger` 決定），或「名單上所有人」。 */
export type EmailFlowTriggerEvent = EmailRuleEventKey | typeof EMAIL_FLOW_AUDIENCE_TRIGGER

/** 屬性規則可判斷的用戶屬性：訂閱狀態、方案、介面語言、主要用途。 */
export const EMAIL_RULE_PROPERTY_KEYS = ['subscription', 'plan', 'locale', 'use_case'] as const
export type EmailRulePropertyKey = (typeof EMAIL_RULE_PROPERTY_KEYS)[number]

/**
 * 訂閱狀態四值：
 * - `none`：沒有有效訂閱，也沒有確定付過款
 * - `active`：訂閱中（含續訂扣款失敗、仍在寬限期）
 * - `canceled`：已取消、但這一期還沒到期
 * - `ended`：付過款，現在沒有有效訂閱
 */
export const EMAIL_SUBSCRIPTION_STATES = ['none', 'active', 'canceled', 'ended'] as const
export type EmailSubscriptionState = (typeof EMAIL_SUBSCRIPTION_STATES)[number]

/** 方案沿用用戶列表的方案篩選值（`plan-limits.ts` 的 `PLAN_CODES`）；`team` 是團隊座位。 */
export const EMAIL_RULE_PLANS = PLAN_CODES
export type EmailRulePlan = (typeof EMAIL_RULE_PLANS)[number]

/** 介面語言。沒有紀錄的用戶當 `zh-TW`。 */
export const EMAIL_RULE_LOCALES = ['zh-TW', 'en'] as const
export type EmailRuleLocale = (typeof EMAIL_RULE_LOCALES)[number]

/** 主要用途：新用戶回答的 `USER_USE_CASES`，加上 `unknown`（沒回答或跳過）。 */
export const EMAIL_RULE_USE_CASES = [...USER_USE_CASES, 'unknown'] as const
export type EmailRuleUseCase = (typeof EMAIL_RULE_USE_CASES)[number]

/** 事件規則的比較方式：`did` 次數 > 0、`not` 次數 = 0、`gte` 次數 ≥ count、`lt` 次數 < count。 */
export const EMAIL_EVENT_RULE_HOWS = ['did', 'not', 'gte', 'lt'] as const
export type EmailEventRuleHow = (typeof EMAIL_EVENT_RULE_HOWS)[number]

/** 事件目錄裡「一次」代表什麼（api `email-rules/event_catalog.ts` 的 `unit`）；顯示文字在前端 i18n。 */
export const EMAIL_RULE_EVENT_UNITS = ['times', 'items', 'days'] as const
export type EmailRuleEventUnit = (typeof EMAIL_RULE_EVENT_UNITS)[number]

/** `email_flow_runs.state`（有 DB CHECK）；`GET /internal/email/flows/:id/runs` 的 `state` 篩選也是這組。 */
export const EMAIL_FLOW_RUN_STATES = ['active', 'completed', 'exited', 'failed'] as const
export type EmailFlowRunState = (typeof EMAIL_FLOW_RUN_STATES)[number]

/** 流程自動儲存遇到版本衝突的 409 `error`：流程列與內容文件。前端把它換成「請重新載入」而不是失敗。 */
export const EMAIL_FLOW_SAVE_CONFLICT_CODES = ['flow_conflict', 'email_version_conflict'] as const

/** 行銷信自動儲存遇到版本衝突的 409 `error`：活動列與內容文件。 */
export const EMAIL_CAMPAIGN_SAVE_CONFLICT_CODES = [
  'campaign_conflict',
  'email_version_conflict',
] as const

/** 條件分支節點的兩個分支鍵，依走訪順序；數字分段與 A/B 的分支鍵是索引，不在這裡。 */
export const EMAIL_FLOW_CONDITION_BRANCHES = ['yes', 'no'] as const
export type EmailFlowConditionBranch = (typeof EMAIL_FLOW_CONDITION_BRANCHES)[number]

/**
 * 一位用戶對行銷信的收信狀態：可以收、已退訂、信箱無效、不寄（帳號未啟用）。由 api 的
 * `email-delivery/recipient_status.ts` 單點推導；用戶列表的篩選值與顯示順序也是這組。
 */
export const MARKETING_RECIPIENT_STATUSES = [
  'ok',
  'unsubscribed',
  'invalid_address',
  'account_inactive',
] as const
export type MarketingRecipientStatus = (typeof MARKETING_RECIPIENT_STATUSES)[number]

/**
 * 收件人被排除的原因；陣列順序就是優先序（一位只算一個原因）：不是用戶 → 帳號未啟用 → 信箱無效
 * → 已退訂 → 不符分群。
 */
export const RECIPIENT_EXCLUSION_REASONS = [
  'not_user',
  'account_inactive',
  'invalid_address',
  'unsubscribed',
  'preset_mismatch',
] as const
export type RecipientExclusionReason = (typeof RECIPIENT_EXCLUSION_REASONS)[number]

/** 依產品使用狀況的六個分群，也是收件人抽屜的顯示順序。 */
export const EMAIL_PRODUCT_AUDIENCE_PRESETS = [
  'active_accounts',
  'no_first_result',
  'transcript_only',
  'voice_only',
  'both_products',
  'paid',
] as const
export type EmailProductAudiencePreset = (typeof EMAIL_PRODUCT_AUDIENCE_PRESETS)[number]

/** 活動設定存的分群：六個產品分群，加上 `custom`（不依產品分群，收件人就是名單上的人）。 */
export const EMAIL_AUDIENCE_PRESETS = ['custom', ...EMAIL_PRODUCT_AUDIENCE_PRESETS] as const
export type EmailAudiencePreset = (typeof EMAIL_AUDIENCE_PRESETS)[number]

/** 行銷信的寄送時機：排程或立即。 */
export const EMAIL_CAMPAIGN_MODES = ['scheduled', 'immediate'] as const
export type EmailCampaignMode = (typeof EMAIL_CAMPAIGN_MODES)[number]

/** `email_campaigns.state`；列表的 `state` 篩選也是這組。 */
export const EMAIL_CAMPAIGN_STATES = [
  'draft',
  'scheduled',
  'running',
  'completed',
  'cancelled',
] as const
export type EmailCampaignState = (typeof EMAIL_CAMPAIGN_STATES)[number]

/**
 * 寄送結果頁的收件結果分類，由 api 的 `email-delivery/campaign_report.ts` 從寄送紀錄的 state 單點推導；
 * 陣列順序是報表各分類的排列順序。
 */
export const EMAIL_DELIVERY_OUTCOMES = [
  'delivered',
  'bounced',
  'excluded',
  'failed',
  'pending',
  'sent',
  'cancelled',
] as const
export type EmailDeliveryOutcome = (typeof EMAIL_DELIVERY_OUTCOMES)[number]

/** `email_contacts.validity`：後台聯絡人的信箱驗證狀態。 */
export const EMAIL_CONTACT_VALIDITY_STATES = ['unchecked', 'pending_verification', 'verified'] as const
export type EmailContactValidityState = (typeof EMAIL_CONTACT_VALIDITY_STATES)[number]

/** 上傳名單（`email_audience_lists.name`）的名稱上限：行銷信名單與流程對象名單共用。 */
export const EMAIL_AUDIENCE_LIST_NAME_MAX = 120

/** 這封信是活動（一次性寄送）還是自動化流程（觸發式）產生的。 */
export const EMAIL_DELIVERY_SOURCE_KINDS = ['campaign', 'flow'] as const
export type EmailDeliverySourceKind = (typeof EMAIL_DELIVERY_SOURCE_KINDS)[number]
